import { EnvironmentProviders, Provider } from '@angular/core';
import { provideJitExtensions, setModelFetcher } from '@jds/jit-extensions';
import { environment } from '../environments/environment';

export type ModelFetcher = (url: string, init?: RequestInit) => Promise<Response>;

/**
 * Real wiring for the JAX toolbar tools -- swapped in for `jax-tools.provider.ts`
 * by every named build configuration (see `fileReplacements` in project.json).
 *
 * One of exactly two files in jit-ui that may *import*
 * `@jds/jit-extensions`: this one for the toolbar, and
 * `jax-engines.provider.jax.ts` for the pipeline engines, which since the
 * plugin's 0.3.0 are implemented in that same package rather than here. Both are
 * file-replacement seams for the same reason -- the default build must not name
 * the private specifier in any position, value or type -- so keep the count at
 * two. Naming it in a comment (as this line does) is free; only imports resolve.
 *
 * `provideJitExtensions` also contributes the "Digital Pathology - DIANNE" toolbar
 * tool (from the plugin's 0.5.0), a dialog tool on the visualizer's `TOOLBAR_TOOLS`
 * whose button sits next to the image processing pipeline. It takes its base URL and
 * auth from `VIZ_CONFIG` and `TILE_ACCESS_PORT`, so nothing extra is passed here.
 *
 * `browserModelServer` is undefined in environments that should not offer the
 * model tools; the plugin then resolves no weights and the toolbar renders no YOLO
 * or retinal buttons, so this stays a single unconditional call. DIANNE downloads no
 * model, so it is offered regardless; pass `tools` to change that.
 */
export function provideJaxToolbarTools(): (Provider | EnvironmentProviders)[] {
  return [provideJitExtensions({ modelBaseUrl: environment.browserModelServer })];
}

/**
 * Hands the Auth0-bearing fetch to the plugin's model downloads.
 *
 * The checkpoints are fetched by the model libraries with `fetch` rather than
 * HttpClient, so the auth interceptor never sees them and they would otherwise
 * 401 against the model server.
 */
export function setJaxToolbarModelFetcher(fetcher: ModelFetcher): void {
  setModelFetcher(fetcher);
}
